import React, { useEffect } from 'react';
import { Layout } from 'antd';
import { Outlet } from 'react-router-dom';
import ResponsiveMenu from '../Menu/ResponsiveMenu';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useMenuState } from '../../hooks/useMenuState';
import './MainLayout.css';

const { Content } = Layout;

// 客服助手开关：true 开启；false 关闭（不加载 SDK、不渲染入口）
const ENABLE_QINGLIU_ASSISTANT = true;

// 智谱 AI 客服助手配置
const QINGLIU_SHARE_URL = 'https://appcenter.bigmodel.cn/console/appcenter_v2/chat?share_code=NJQd3BXThRwiYWrKK5bId';
const QINGLIU_SDK_SRC = 'https://cdn.bigmodel.cn/knowledge/js/sdk.min.js';
const QINGLIU_SDK_SCRIPT_ID = 'qingliu-knowledge-sdk';
const QINGLIU_ROOT_ID = 'qingliu-assistant-root';
const QINGLIU_GLOBAL_KEY = '__SHOWY_QINGLIU_ASSISTANT__';

// 确保智谱 Web SDK 已加载：优先复用已存在的全局对象或已插入的 script
function ensureQingliuSdkLoaded() {
  if (window.QINGLIU && window.QINGLIU.SDK) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(QINGLIU_SDK_SCRIPT_ID);
    if (existing) {
      const timer = window.setInterval(() => {
        if (window.QINGLIU && window.QINGLIU.SDK) {
          window.clearInterval(timer);
          resolve();
        }
      }, 100);
      window.setTimeout(() => {
        window.clearInterval(timer);
        reject(new Error('QINGLIU SDK load timeout'));
      }, 12000);
      return;
    }

    const script = document.createElement('script');
    script.id = QINGLIU_SDK_SCRIPT_ID;
    script.src = QINGLIU_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('QINGLIU SDK load failed'));
    document.body.appendChild(script);
  });
}

const MainLayout = () => {
  const { isMobile } = useBreakpoint();
  const { sidebarCollapsed } = useMenuState();

  useEffect(() => {
    // 关闭开关时，不加载 SDK、不初始化助手
    if (!ENABLE_QINGLIU_ASSISTANT) return;

    // 使用全局状态避免 React 18 StrictMode 或路由重挂载导致重复初始化
    const globalState = window[QINGLIU_GLOBAL_KEY] || (window[QINGLIU_GLOBAL_KEY] = {});
    if (globalState.inited || globalState.initializing) return;

    globalState.initializing = true;

    ensureQingliuSdkLoaded()
      .then(() => {
        // SDK 加载完成后再创建实例，避免点击入口无响应
        if (!window.QINGLIU || !window.QINGLIU.SDK) return;
        const root = document.getElementById(QINGLIU_ROOT_ID);
        if (!root) return;

        // 初始化：dialog 模式 + 右下角悬浮入口
        globalState.sdk = new window.QINGLIU.SDK({
          type: 'dialog',
          root: QINGLIU_ROOT_ID,
          shareUrl: QINGLIU_SHARE_URL,
          width: 420,
          height: 620,
          bubble: {
            placement: 'right',
            bottom: 24,
          },
        });
        globalState.sdk.init();
        globalState.inited = true;
      })
      .finally(() => {
        globalState.initializing = false;
      });
  }, []);

  return (
    <Layout className="main-layout">
      <ResponsiveMenu />
      <Layout 
        className={`content-layout ${isMobile ? 'mobile' : 'desktop'} ${sidebarCollapsed ? 'collapsed' : ''}`}
      >
        <Content className="main-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
      {/* 客服助手挂载点：仅开启开关时渲染 */}
      {ENABLE_QINGLIU_ASSISTANT ? <div id={QINGLIU_ROOT_ID} /> : null}
    </Layout>
  );
};

export default MainLayout;
